import React, {useEffect, useRef, useState} from 'react';
import {Box, IconButton, InputAdornment, TextField, useColorScheme} from '@mui/material';
import {Send} from '@mui/icons-material';
import {useChatContext} from '../context';
import dayjs from 'dayjs';
import {Permission, usePermissions} from 'utils/hooks/permissions/usePermissions';

export const ChatEnterField = () => {
    const {colorScheme} = useColorScheme();
    const theme = colorScheme ? colorScheme : 'dark';

    const {sendMessage, selectedChat} = useChatContext();
    const [newMessage, setNewMessage] = useState('');

    const isGena = selectedChat?.type === 'GENA';
    const {roles} = usePermissions();
    const canUseGena = roles?.includes(Permission.CAN_USE_GENA);
    const disabled = isGena && !canUseGena;

    const inputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (!selectedChat?.id || disabled) return;
        const raf = requestAnimationFrame(() => {
            const el = inputRef.current;
            if (!el) return;
            el.focus({preventScroll: true});
            const len = el.value.length;
            el.setSelectionRange?.(len, len);
        });
        return () => cancelAnimationFrame(raf);
    }, [selectedChat?.id, disabled]);

    useEffect(() => {
        setNewMessage('');
    }, [selectedChat?.id]);

    const handleSendMessage = () => {
        if (!newMessage.trim() || !selectedChat) return;
        sendMessage({
            content: newMessage.trim(),
            type: 'TEXT',
            dateTime: dayjs().toISOString(),
        });
        setNewMessage('');
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                padding: '12px 0',
            }}
        >
            <TextField
                disabled={disabled}
                fullWidth
                variant='outlined'
                placeholder={disabled ? 'У вас нет доступа к AI ассистенту' : 'Введите сообщение'}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={handleKeyPress}
                inputRef={inputRef}
                autoFocus={!disabled}
                multiline
                sx={{
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                            border: 'none',
                        },
                        '&:hover fieldset': {
                            border: 'none',
                        },
                        '&.Mui-focused fieldset': {
                            border: 'none',
                        },
                    },
                    borderRadius: '5px',
                    backgroundColor: `${theme === 'dark' ? 'var(--background-paper-elevation-11, #313131)' : '#FFFFFF'}`,
                }}
                maxRows={10}
                slotProps={{
                    htmlInput: {maxLength: 4096},
                    input: {
                        endAdornment: (
                            <InputAdornment position='end'>
                                <IconButton
                                    onClick={handleSendMessage}
                                    disabled={!newMessage.trim()}
                                    edge='end'
                                    color='primary'
                                >
                                    <Send />
                                </IconButton>
                            </InputAdornment>
                        ),
                    },
                }}
            />
        </Box>
    );
};
